import React from "react";
import {
  Button,
  Center,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, CopyIcon } from "@chakra-ui/icons";
import { FaDownload } from "react-icons/fa";
import TradingSpinner from "../TradingSpinner";
import PerformanceCardCanvas from "../PerformanceCardCanvas";

/**
 * The Performance Card modal (preview + share/download buttons) plus the
 * off-screen `<PerformanceCardCanvas>` used by html-to-image to actually
 * generate the dataURL. The off-screen card needs a `ref`, so the parent
 * forwards `cardRef` here and the modal's image source is the parent-owned
 * `performanceCardDataUrl` state.
 */
const PerformanceCardModal = ({
  isOpen,
  onClose,
  isLoading,
  dataUrl,
  shareLinkCopied,
  onCopyShareLink,
  renderCard,
  cardRef,
  user,
  stats,
  accountValues,
  planAdherence,
  riskReward,
  accountValueMetrics,
  journalStreak,
}) => {
  const displayName = user.username
    ? `@${user.username}`
    : `${user.first_name} ${user.last_name?.[0]}.`;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Performance Card</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading ? (
              <Center py={8}>
                <TradingSpinner size="40px" />
              </Center>
            ) : dataUrl ? (
              <VStack spacing={4}>
                <Image
                  src={dataUrl}
                  alt="Performance Card"
                  borderRadius="md"
                  maxWidth="100%"
                />
                <HStack spacing={3}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={shareLinkCopied ? <CheckIcon /> : <CopyIcon />}
                    onClick={onCopyShareLink}
                  >
                    {shareLinkCopied ? "Copied!" : "Copy Profile Link"}
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="teal"
                    leftIcon={<Icon as={FaDownload} />}
                    as="a"
                    href={dataUrl}
                    download="performance-card.png"
                  >
                    Download
                  </Button>
                </HStack>
              </VStack>
            ) : (
              <Text>Unable to generate performance card. Please try again.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {renderCard && (
        <div style={{ position: "fixed", left: "-9999px", top: "-9999px" }}>
          <PerformanceCardCanvas
            ref={cardRef}
            displayName={displayName}
            stats={stats?.stats || {}}
            accountValues={accountValues}
            planAdherence={planAdherence}
            riskReward={riskReward}
            accountValueMetrics={accountValueMetrics}
            journalStreak={journalStreak}
          />
        </div>
      )}
    </>
  );
};

export default PerformanceCardModal;
